import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Raven from 'raven-js';
import { useDispatch } from 'react-redux';
import { addQueryParam } from '../utils/AddQueryParam';
import { markNotificationAsDelivered, markNotificationAsDismissed, markNotificationAsRead } from '../utils/TrackingUtils';
import { clearNotificationTimeout } from '../utils/AnimationUtil';
import { DELIVERY_METHOD_PARAM, DISPLAY_STATE_TRANSITIONING, DISPLAY_STATE_CLOSED, DISPLAY_STATE_OPEN } from '../constants';
import { markAsDelivered, markNotificationAsReadApi } from '../api/notificationsApi';
import { removeNotification } from '../RealTimeNotificationHandlers/Reducer';
export const useFloatingNotificationState = ({
  initialDisplayState,
  deliveryMethod,
  notification,
  autoCloseDuration
}) => {
  const [displayState, setDisplayState] = useState(initialDisplayState);
  const [isHovered, setIsHovered] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const dispatch = useDispatch();
  const closeTimeoutRef = useRef(null);
  const removeNotificationHandler = useCallback(() => {
    dispatch(removeNotification({
      notification,
      deliveryMethod
    }));
  }, [deliveryMethod, dispatch, notification]);
  const handleCloseNotification = useCallback(() => {
    clearNotificationTimeout(closeTimeoutRef);
    setDisplayState(DISPLAY_STATE_TRANSITIONING);
  }, []);
  const handleAnimationEnd = useCallback(() => {
    if (displayState === DISPLAY_STATE_TRANSITIONING) {
      setDisplayState(DISPLAY_STATE_CLOSED);
      removeNotificationHandler();
    }
  }, [displayState, removeNotificationHandler]);
  const linkUrl = useMemo(() => {
    return addQueryParam({
      url: notification.ctaProxyUrl,
      key: DELIVERY_METHOD_PARAM,
      val: deliveryMethod
    });
  }, [deliveryMethod, notification.ctaProxyUrl]);
  const handleCloseOnClick = useCallback(() => {
    markNotificationAsDismissed(notification, deliveryMethod);
    handleCloseNotification();
  }, [deliveryMethod, handleCloseNotification, notification]);

  //  Close notification after timeout / handle pausing dismiss on hover
  useEffect(() => {
    if (displayState === DISPLAY_STATE_OPEN && autoCloseDuration && !isHovered) {
      closeTimeoutRef.current = setTimeout(handleCloseNotification, autoCloseDuration);
    } else if (isHovered && closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    return () => clearNotificationTimeout(closeTimeoutRef);
  }, [displayState, handleCloseNotification, autoCloseDuration, isHovered]);

  //  Initial markAsDelivered on render
  useEffect(() => {
    if (displayState === DISPLAY_STATE_OPEN) {
      markNotificationAsDelivered(notification, deliveryMethod);
      markAsDelivered({
        notificationId: notification.id,
        deliveryMethod
      }).catch(e => Raven.captureException(e));
    }
  }, [deliveryMethod, displayState, notification]);

  //  Mark notification as read on visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !hasRead && displayState === DISPLAY_STATE_OPEN) {
        markNotificationAsRead(notification, deliveryMethod);
        markNotificationAsReadApi({
          notificationId: notification.id,
          deliveryMethod
        }).catch(e => Raven.captureException(e));
        setHasRead(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    handleVisibilityChange();
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [notification, deliveryMethod, displayState, hasRead]);
  return {
    displayState,
    handleCloseOnClick,
    handleCloseNotification,
    handleAnimationEnd,
    setIsHovered,
    isHovered,
    linkUrl
  };
};